import { Location } from 'react-router-dom';
import { ProjectDefinitionQueryResult } from 'src/app/projects/views/project-list/state/api/queries/projectsListQuery';
import analyticsTracking from 'src/services/analytics-service';
import { GtagUserRoleFlags, TrafficUserType, defaultGtagConfig } from 'src/services/google-analytics';
import { Role } from 'src/shared/types/graph/user';

// Push customer/project/user context to GA on route changes.

export type SetRouteAnalyticsParams = {
    location: Location;
    isAuthenticated: boolean;
    projectDefinition?: ProjectDefinitionQueryResult | null;
    isCurrentUserInternal?: boolean;
    currentCustomerName?: string;
    currentCustomerId?: string;
    currentProjectId?: string;
    currentCustomerRoles?: Role[];
    currentProjectRoles?: Role[];
    currentUserEmail?: string;
};

// Union of customer- and project-scope roles → one boolean per Role.
// Callers pass an empty projectRoles to clear project-scoped state on routes
// that have left the project context (projects list, admin).
const roleFlags = (customerRoles: Role[] = [], projectRoles: Role[] = []): GtagUserRoleFlags => {
    const active = new Set<Role>([...customerRoles, ...projectRoles]);
    return {
        role_admin: active.has(Role.ADMIN),
        role_customer_admin: active.has(Role.CUSTOMER_ADMIN),
        role_nm_customer_manager: active.has(Role.NM_CUSTOMER_MANAGER),
        role_project_admin: active.has(Role.PROJECT_ADMIN),
        role_nm_project_admin: active.has(Role.NM_PROJECT_ADMIN),
        role_project_viewer: active.has(Role.PROJECT_VIEWER),
    };
};

// Email domains considered internal to Nature Metrics. Extend this list as needed.
const INTERNAL_EMAIL_DOMAINS = ['naturemetrics.co.uk'];

const isInternalByEmail = (email?: string): TrafficUserType => {
    if (!email) {
        return TrafficUserType.NOT_SET;
    }
    const domain = email.toLowerCase().split('@')[1];
    if (!domain) {
        return TrafficUserType.NOT_SET;
    }
    return INTERNAL_EMAIL_DOMAINS.includes(domain) ? TrafficUserType.INTERNAL : TrafficUserType.EXTERNAL;
};

const setRouteAnalytics = (params: SetRouteAnalyticsParams) => {
    const {
        location,
        isAuthenticated,
        projectDefinition,
        isCurrentUserInternal,
        currentCustomerName,
        currentCustomerId,
        currentProjectId,
        currentCustomerRoles,
        currentProjectRoles,
        currentUserEmail,
    } = params;

    const isProjectsLocation = location.pathname.includes('/projects') && !location.pathname.includes('/project-definition');
    const isAdminLocation = location.pathname.includes('/admin');
    const shouldRemoveProject = isProjectsLocation || isAdminLocation;
    const gaCustomerValue = currentCustomerName || defaultGtagConfig.customer_name;
    const gaCustomerIdValue = currentCustomerId || defaultGtagConfig.customer_id;
    const gaProjectValue = projectDefinition?.projectName || defaultGtagConfig.project;
    const gaProjectIdValue = currentProjectId || defaultGtagConfig.project_id;
    const gaTrafficUserTypeValue = isCurrentUserInternal ? TrafficUserType.INTERNAL : TrafficUserType.EXTERNAL;

    // User properties must be set before updateConfig: gtag('config') emits a
    // page_view, and that page_view only carries the user properties already
    // present on the client. Setting them afterwards attaches them to the next
    // page_view instead, which left them absent from ~98% of page_views.
    analyticsTracking.setUserProperties({
        ...roleFlags(currentCustomerRoles, shouldRemoveProject ? [] : currentProjectRoles),
        email_domain_internal_user: isInternalByEmail(currentUserEmail),
    });

    analyticsTracking.updateConfig({
        traffic_user_type: isAuthenticated ? gaTrafficUserTypeValue : defaultGtagConfig.traffic_user_type,
        customer_name: gaCustomerValue,
        customer_id: gaCustomerIdValue,
        project: !shouldRemoveProject ? gaProjectValue : defaultGtagConfig.project,
        project_id: !shouldRemoveProject ? gaProjectIdValue : defaultGtagConfig.project_id,
    });
};

export default setRouteAnalytics;
